/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useRef} from 'react';
import useHasMounted from '@ion/core/src/hooks/useHasMounted';
import {getLayerByTitle} from '../helpers/MapViewUtils';

// Defines the LayerAutoUpdate props
interface LayerAutoUpdateProps {
  layers: __esri.Layer[],
  title: string,
  timeInterval: number,
  callback?: () => void
};

/**
 * @useLayerAutoUpdate
 *
 * Provides a hook for helping layer auto updates functionality
 *
 * ```typescript
 *
 *    const {
 *      timerUpdateRef,
 *      updateIntervalRef,
 *      startUpdate,
 *      stopUpdate
 *    } = useLayerAutoUpdate(layers, title, interval, () => {});
 *    ...
 * ```
 */
export const useLayerAutoUpdate = ({
  layers,
  title,
  timeInterval = 12000,
  callback
}: LayerAutoUpdateProps) => {
  const timerUpdateRef    = useRef(null);
  const updateIntervalRef = useRef(timeInterval);
  const isMounted         = useHasMounted();

  useEffect(() => stopUpdate, []);

  useEffect(() => {
    if (!isMounted) {
      const {refreshInterval} = getLayerByTitle(layers, title) || {};
      if (refreshInterval) {
        updateIntervalRef.current = (refreshInterval * 60000);
      }
    }
  }, [isMounted]);

  const startUpdate = () => {
    timerUpdateRef.current = setInterval(callback, updateIntervalRef.current);
  };

  const stopUpdate = () => {
    clearInterval?.(timerUpdateRef.current);
    timerUpdateRef.current = null;
  };

  return {timerUpdateRef, updateIntervalRef, startUpdate, stopUpdate};
};

export default useLayerAutoUpdate;
